import type { ManagedAgent } from '~/hooks/queries/use-agent-management';
import type {
  CustomAgentSummary,
  CustomAgentVisibility,
} from '~/hooks/queries/use-workspace-custom-agents';
import { inlineMarkdownToHtml } from '~/lib/markdown-inline';

/**
 * FRONT-2406: pure classification/sorting/filtering/persistence logic behind
 * the Agents page's five tabs (All · My agents · Shared · Org private ·
 * Predefined; Org private is admin-only, round 2 section B).
 * Deliberately no React, no queries, no DOM — every function here takes
 * already-resolved data and returns a plain value, so the whole surface can
 * be swept in `agents-overview-model.test.ts` without mounting anything.
 * `agents-overview.tsx` is the only caller; it owns loading/error states and
 * wiring the mutations.
 */

export type AgentTabValue = 'all' | 'mine' | 'shared' | 'private' | 'predefined';

/**
 * Canonical order and the full valid-value set (used for URL/store
 * normalization) — includes `private` even though it is admin-only; the
 * *rendering* filter (`visibleAgentTabValues`) is what hides it from a
 * non-admin, kept separate from what counts as a VALID tab value at all.
 */
export const AGENT_TAB_VALUES: readonly AgentTabValue[] = [
  'all',
  'mine',
  'shared',
  'private',
  'predefined',
];

/**
 * FRONT-2406 round 2: the tabs a viewer actually SEES. `private` (a
 * colleague's `private`/`restricted` agent, visible only through an admin's
 * governance read — see `isUnusableForeignAgent`) is admin-only; a non-admin
 * never sees the tab, even though its rows still show up under All for
 * whichever admins can see them.
 */
export function visibleAgentTabValues(isAdmin: boolean): AgentTabValue[] {
  return isAdmin ? [...AGENT_TAB_VALUES] : AGENT_TAB_VALUES.filter((v) => v !== 'private');
}

export type AgentOrigin = 'predefined' | 'mine' | 'shared' | 'private';

/** One row, whatever its origin — the row component renders ONE shape. */
export interface AgentRowModel {
  id: string;
  name: string;
  description: string;
  origin: AgentOrigin;
  /** Predefined only. `undefined` for a custom row — the Connected column reads "—". */
  isConfigured?: boolean;
  /** Custom only. */
  visibility?: CustomAgentVisibility;
  createdByZitadelUserId?: string;
  /** FRONT-1202: someone else's `private` agent, visible only via an admin's governance read. */
  isForeignPrivate: boolean;
  /** Custom only — this agent is on the tenant's disabled-custom-agents denylist. */
  workspaceDisabled: boolean;
  /** Predefined only (FRONT-602) — `isPredefinedAgentUnavailable`. */
  unavailable: boolean;
  /** The "On for me" switch's checked state, before `rowSwitchGate` is applied. */
  personalOn: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

// ---------------------------------------------------------------------------
// Predefined (built-in) agents
// ---------------------------------------------------------------------------

/**
 * Predefined agents, filtered to the tenant-enabled set — the same filter
 * the personal roster has always applied. `null` means "everything available
 * is enabled" (the legacy default semantic `useEnabledAgentIds` documents).
 */
export function classifyPredefinedAgents(
  agents: ManagedAgent[],
  enabledAgentIds: string[] | null,
): ManagedAgent[] {
  if (enabledAgentIds === null) return agents;
  const enabled = new Set(enabledAgentIds);
  return agents.filter((a) => enabled.has(a.id));
}

/**
 * FRONT-602: the capability ids the person could connect themselves — every
 * capability of a provider the Connections page offers as set up for this
 * workspace. A provider with `configured: false` is listed there too, but
 * nobody in the workspace can connect it until an admin sets it up; and one
 * provider can carry a capability the platform switched off (`active` or
 * `available` false) next to one it did not. An absent flag counts as on, so
 * a payload without them never greys a row.
 */
export function connectableCapabilityIds(
  providers: ReadonlyArray<{
    configured: boolean;
    capabilities: ReadonlyArray<{ id: string; active?: boolean; available?: boolean }>;
  }>,
): Set<string> {
  const ids = new Set<string>();
  for (const p of providers) {
    if (!p.configured) continue;
    for (const c of p.capabilities) {
      if (c.active === false || c.available === false) continue;
      ids.add(c.id);
    }
  }
  return ids;
}

/**
 * FRONT-602: true when this predefined agent cannot run for the viewer AND
 * the person cannot fix that on the Connections page — the roster says it is
 * not configured, and one of its REQUIRED capabilities has no connectable
 * provider at all (DATEV, Statista and the like, which the
 * platform sets up or nobody does). An agent missing only a connection the
 * person could make (Gmail) stays live: its "Not connected" badge already
 * says so, and greying it would hide the one place they would act.
 *
 * "Required" is the backend's own definition (`requiredCapabilityIds`,
 * src/tools/delegation-outcome.ts — not importable from here): an ACTIVE
 * capability (static, or selected) that the roster marks `connectionRequired`.
 *
 * Every unknown answers false, never "unavailable": providers not loaded
 * (`null`), `isConfigured` absent, or no required capability marked. Greying
 * a working agent off is worse than leaving a dead one switchable — the
 * runtime refuses an unconfigured agent with its own message regardless.
 *
 * ponytail: known ceiling — the roster's `isConfigured` is one flag per agent,
 * so an agent whose platform-only capability IS set up but which also misses
 * a connection the person could make (say Gmail) reads as unavailable too.
 * The exact answer needs the per-caller live connection check the backend
 * runs at delegation time (`checkRequiredConnections`); moving this decision
 * server-side onto `/api/agents` is the upgrade path.
 */
export function isPredefinedAgentUnavailable(
  agent: Pick<ManagedAgent, 'isConfigured' | 'capabilities'>,
  connectable: ReadonlySet<string> | null,
): boolean {
  if (connectable === null || agent.isConfigured !== false) return false;
  type Cap = { activation?: unknown; selected?: unknown; connectionRequired?: unknown } | null;
  const required = Object.entries(agent.capabilities ?? {})
    .filter(([, raw]) => {
      const cap = raw as Cap;
      const active = cap?.activation === 'static' || cap?.selected === true;
      return active && cap?.connectionRequired === true;
    })
    .map(([id]) => id);
  return required.some((id) => !connectable.has(id));
}

export function buildPredefinedRows(
  agents: ManagedAgent[],
  enabledAgentIds: string[] | null,
  myDisabledIds: string[],
  /** `connectableCapabilityIds(...)`, or `null` while unknown (FRONT-602). */
  connectable: ReadonlySet<string> | null = null,
): AgentRowModel[] {
  const myDisabledSet = new Set(myDisabledIds);
  return classifyPredefinedAgents(agents, enabledAgentIds).map(
    (a): AgentRowModel => ({
      id: a.id,
      name: a.name || a.id,
      description: a.description ?? '',
      origin: 'predefined',
      isConfigured: a.isConfigured,
      isForeignPrivate: false,
      workspaceDisabled: false,
      unavailable: isPredefinedAgentUnavailable(a, connectable),
      personalOn: !myDisabledSet.has(a.id),
      canEdit: false,
      canDelete: false,
    }),
  );
}

// ---------------------------------------------------------------------------
// Custom agents
// ---------------------------------------------------------------------------

/**
 * Owner-only ACTION gate (edit / revert / share). When `visibility` is
 * undefined the visibility feature is off → legacy behaviour, no gating. When
 * present, only the author may perform owner-only actions. Mirrors the
 * server-side rule (PDEV-6450); the backend stays the source of truth.
 *
 * Moved verbatim from `features/admin/custom-agents-section.tsx` (FRONT-2406)
 * — that module is retired, this is its one home now.
 */
export function ownsOrLegacy(
  agent: CustomAgentSummary | undefined,
  currentUserId: string | undefined,
): boolean {
  if (!agent) return false;
  if (agent.visibility === undefined) return true;
  return !!currentUserId && agent.createdByZitadelUserId === currentUserId;
}

/**
 * FRONT-1202, widened by FRONT-2406 fix round 1: true when this row is
 * someone else's `private` OR `restricted` agent — visible only because an
 * admin's list read is a governance read of the whole org, and not usable by
 * the viewer under any setting.
 *
 * Deliberately narrower than "not mine". `private` is usable by its owner
 * alone; `restricted` by its owner plus anyone holding a resource_shares
 * grant (FRONT-1650, `src/agents/custom-agent-visibility.ts`). An admin's
 * list carries every agent in the org, grant or not, so for an admin both
 * values stay filed here; a non-admin only ever receives a colleague's
 * `restricted` agent through a grant (the `isAdmin` branch below). For
 * anything left, the enable switch is a lie: delegation
 * refuses it by id and it never enters that user's available-agents list.
 * `org` agents are usable by the whole workspace. Owner-unknown (no
 * `currentUserId` yet) stays unlocked for the same reason.
 *
 * Moved verbatim from `features/admin/custom-agents-section.tsx` (FRONT-2406),
 * then widened from `visibility === 'private'` to `visibility !== 'org'` when
 * FRONT-2406's own fix round found the `restricted` case wasn't actually
 * shareable yet either (see the corrected reasoning above — the OLD comment
 * here claimed a `restricted` agent "may well be shared with the viewer",
 * which was wrong: the server enforces owner-only for it exactly like
 * `private`, so treating it as usable was the bug, not a cautious default).
 */
export function isUnusableForeignAgent(
  agent: CustomAgentSummary,
  currentUserId: string | undefined,
  isAdmin = true,
): boolean {
  // FRONT-1650: the server hands a non-admin a colleague's `restricted` agent
  // only when a resource_shares grant lets them use it. An admin's list is
  // the whole org, so for an admin it stays filed as unusable.
  if (!isAdmin && agent.visibility === 'restricted') return false;
  return (
    agent.visibility !== undefined &&
    agent.visibility !== 'org' &&
    !!currentUserId &&
    agent.createdByZitadelUserId !== currentUserId
  );
}

/**
 * FRONT-2406 tab CLASSIFICATION — which of the four origins a custom agent's
 * row is. Deliberately a different test than `ownsOrLegacy`: that gate
 * decides who may edit/delete a row (and treats a legacy agent with no
 * `visibility` as everyone's to manage); this decides where the row is
 * FILED and how it's badged.
 *
 * `private` (fix round 1) is the same predicate as `isUnusableForeignAgent`
 * on purpose — a colleague's agent unusable for this viewer is never
 * "Shared", it just happens to be visible via an admin's governance read.
 * Everything else NOT created by the viewer — `org`-visible, or the legacy
 * `visibility === undefined` — is `shared`, even though a legacy agent's
 * actions are ungated for everyone (`ownsOrLegacy` says "you may manage it",
 * this still files it under Shared because you did not create it).
 */
export function classifyCustomOrigin(
  agent: CustomAgentSummary,
  currentUserId: string | undefined,
  isAdmin = true,
): 'mine' | 'shared' | 'private' {
  if (currentUserId !== undefined && agent.createdByZitadelUserId === currentUserId) return 'mine';
  if (isUnusableForeignAgent(agent, currentUserId, isAdmin)) return 'private';
  return 'shared';
}

export function isWorkspaceDisabledCustomAgent(
  agent: CustomAgentSummary,
  disabledIds: string[],
): boolean {
  return disabledIds.includes(agent.id);
}

/**
 * FRONT-2406: a workspace-disabled custom agent is worth showing only to
 * someone who can act on it — its own owner, or an admin who can flip it back
 * on in Org Settings. Anyone else's disabled agent stays hidden, same as the
 * personal roster's pre-existing behaviour for disabled agents in general.
 *
 * Takes the full `AgentOrigin` union (fix round 1 added `private`) even
 * though only `mine`/`shared` origins can ever be workspace-disabled in
 * practice (`predefined` never calls this; `private` rows are an admin
 * governance read, orthogonal to the tenant denylist) — narrowing the
 * parameter type would just move the cast to every call site.
 */
export function isCustomAgentVisibleToViewer(
  origin: AgentOrigin,
  workspaceDisabled: boolean,
  isAdmin: boolean,
): boolean {
  return !workspaceDisabled || origin === 'mine' || isAdmin;
}

/**
 * FRONT-2406 fix round 1 (item 1): Edit, History and Delete all additionally
 * require builder access, on top of the existing ownership/admin gate — a
 * user demoted from builder who still owns a legacy agent (or still admins)
 * must stop seeing any of the three, not keep them because `ownsOrLegacy`
 * alone says yes. AND-ed in here (not left to the component) so the one
 * pure function is what both the row's buttons AND any future caller agree
 * on — `canEdit`/`canDelete` can never disagree with `canAuthorAgents` again.
 */
export function buildCustomRows(
  agents: CustomAgentSummary[],
  disabledIds: string[],
  myDisabledIds: string[],
  currentUserId: string | undefined,
  isAdmin: boolean,
  canAuthorAgents: boolean,
): AgentRowModel[] {
  const myDisabledSet = new Set(myDisabledIds);
  const rows: AgentRowModel[] = [];
  for (const agent of agents) {
    const origin = classifyCustomOrigin(agent, currentUserId, isAdmin);
    const workspaceDisabled = isWorkspaceDisabledCustomAgent(agent, disabledIds);
    if (!isCustomAgentVisibleToViewer(origin, workspaceDisabled, isAdmin)) continue;
    const owned = ownsOrLegacy(agent, currentUserId);
    // A custom agent (one with a `visibility`) an admin governs on behalf of
    // its owner — predefined rows have no `visibility` and are never admin-deletable here.
    const adminGovernsIt = agent.visibility !== undefined && isAdmin;
    rows.push({
      id: agent.id,
      name: agent.name,
      description: agent.description ?? '',
      origin,
      visibility: agent.visibility,
      createdByZitadelUserId: agent.createdByZitadelUserId,
      isForeignPrivate: isUnusableForeignAgent(agent, currentUserId, isAdmin),
      workspaceDisabled,
      unavailable: false,
      personalOn: !myDisabledSet.has(agent.id),
      canEdit: owned && canAuthorAgents,
      canDelete: canAuthorAgents && (owned || adminGovernsIt),
    });
  }
  return rows;
}

// ---------------------------------------------------------------------------
// Combine / sort / filter / counts
// ---------------------------------------------------------------------------

/** Predefined first, then custom — each group alphabetical (today's order feel). */
export function sortAgentRows(rows: AgentRowModel[]): AgentRowModel[] {
  const rank = (r: AgentRowModel): number => (r.origin === 'predefined' ? 0 : 1);
  return rows.slice().sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

export function filterAgentRowsForTab(rows: AgentRowModel[], tab: AgentTabValue): AgentRowModel[] {
  if (tab === 'all') return rows;
  return rows.filter((r) => r.origin === tab);
}

export interface AgentTabCounts {
  all: number | undefined;
  mine: number | undefined;
  shared: number | undefined;
  private: number | undefined;
  predefined: number | undefined;
}

/**
 * A tab's count is blank (`undefined`), never `0`, until the data behind it
 * has actually loaded — "0" is a claim, and while pending it would be a false
 * one. "All" depends on BOTH sources, so it stays blank until both have.
 *
 * `rows` is whatever set the caller wants counted — round 2 passes the
 * SEARCH-filtered rows here (see `agents-overview.tsx`), so a tab's count
 * reflects the current search term, not just its tab filter. `private` rows
 * are custom-origin, so its count gates on `loaded.custom` like `mine`/`shared`.
 */
export function computeAgentTabCounts(
  rows: AgentRowModel[],
  loaded: { predefined: boolean; custom: boolean },
): AgentTabCounts {
  return {
    predefined: loaded.predefined ? rows.filter((r) => r.origin === 'predefined').length : undefined,
    mine: loaded.custom ? rows.filter((r) => r.origin === 'mine').length : undefined,
    shared: loaded.custom ? rows.filter((r) => r.origin === 'shared').length : undefined,
    private: loaded.custom ? rows.filter((r) => r.origin === 'private').length : undefined,
    all: loaded.predefined && loaded.custom ? rows.length : undefined,
  };
}

// ---------------------------------------------------------------------------
// Search (FRONT-2406 round 2, section C — narrowed by round-2 review item 7)
// — client-side only, case-insensitive (toLocaleLowerCase so it treats
// German umlauts correctly), trimmed.
// ---------------------------------------------------------------------------

/**
 * Strips every HTML tag `inlineMarkdownToHtml` can produce, decoding the
 * handful of entities its own `escapeHtml`/marked-escaping can introduce.
 * Not a general HTML-to-text converter — it only has to undo what THAT
 * function does, since it is always fed that function's own output.
 */
function stripTags(html: string): string {
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The description AS THE ROW DISPLAYS IT: run through the exact same
 * `inlineMarkdownToHtml` call `AgentRow`'s `<InlineMarkdown>` renders with
 * (`frontend/src/components/inline-markdown.tsx`), then the resulting HTML's
 * tags stripped back to plain words. Reusing the real render path — rather
 * than a second, hand-rolled Markdown-stripping regex — is what keeps this
 * function honest about matching what a person actually SEES: a link's URL,
 * a heading's `#`s, and `**`/`_` emphasis markers all disappear from search
 * exactly because they disappear from the row.
 */
export function displayedAgentDescription(description: string): string {
  return stripTags(inlineMarkdownToHtml(description));
}

/**
 * Matches the agent's NAME and its description AS DISPLAYED — never the raw
 * Markdown source, and never the agent's id (round-2 review: a person can't
 * see an id anywhere on this page, so it isn't something they could type to
 * find a row; the earlier version matched it anyway).
 */
export function matchesAgentSearch(row: AgentRowModel, term: string): boolean {
  const q = term.trim().toLocaleLowerCase();
  if (!q) return true;
  return (
    row.name.toLocaleLowerCase().includes(q) ||
    displayedAgentDescription(row.description).toLocaleLowerCase().includes(q)
  );
}

// ---------------------------------------------------------------------------
// Tab resolution (URL param vs. remembered device preference)
// ---------------------------------------------------------------------------

/** Pre-FRONT-2406 tab values, mapped onto their nearest new tab. */
const LEGACY_TAB_ALIASES: Record<string, AgentTabValue> = {
  custom: 'mine',
  roster: 'all',
};

function normalizeAgentTab(value: string | null | undefined): AgentTabValue | null {
  if (!value) return null;
  if ((AGENT_TAB_VALUES as readonly string[]).includes(value)) return value as AgentTabValue;
  return LEGACY_TAB_ALIASES[value] ?? null;
}

/**
 * Precedence: a valid (or legacy-aliased) `?tab=` URL param always wins —
 * it's the deep-linkable, shareable form. Otherwise fall back to the
 * remembered device preference, if that is itself valid. Otherwise `all`.
 *
 * Both inputs go through the SAME normalizer, so an invalid or unrecognised
 * value in either position degrades the same way rather than throwing or
 * rendering a tab that doesn't exist.
 *
 * FRONT-2406 round 2: `private` (the "Org private" tab) is admin-only. Once
 * the URL-vs-remembered precedence above has picked a value, a non-admin who
 * lands on `private` either way — a stale/shared URL, or a remembered value
 * from a session that WAS admin — is bounced to `all` rather than shown a tab
 * they cannot use. This check runs LAST, after normal resolution, so it
 * degrades a `private` result from either source identically, exactly like
 * `normalizeAgentTab` already does for an invalid/legacy one.
 */
export function resolveAgentsTab(
  urlParam: string | null | undefined,
  remembered: string | null | undefined,
  isAdmin: boolean,
): AgentTabValue {
  const resolved = normalizeAgentTab(urlParam) ?? normalizeAgentTab(remembered) ?? 'all';
  if (resolved === 'private' && !isAdmin) return 'all';
  return resolved;
}

// ---------------------------------------------------------------------------
// "On for me" switch gate (CLAUDE.md FRONT-1670: disabled and its
// explanation are ONE decision, never two independently-drifting expressions)
// ---------------------------------------------------------------------------

export const AGENT_SWITCH_REASON_FOREIGN_PRIVATE = 'Private to its owner.';
export const AGENT_SWITCH_REASON_WORKSPACE_DISABLED = 'Switched off for the workspace.';
/** FRONT-602: a required tool nobody here can connect (`isPredefinedAgentUnavailable`). */
export const AGENT_SWITCH_REASON_UNAVAILABLE = 'Not set up for this workspace.';
/** FRONT-2406 round 2 review (item 4): shown on a switchable row's own
 *  switch while the per-tab bulk "All on for me" master switch's request is
 *  in flight. Row writes already serialize per request at the database
 *  (`setUserAgentsEnabled`'s row lock, `src/agents/user-agent-prefs-store.ts`)
 *  so this isn't protecting data — but a control the person can still click
 *  mid-bulk-write, with no visible sign anything is happening, reads as
 *  broken, and CLAUDE.md forbids a disabled control with no reason either
 *  way. Same text as `MASTER_SWITCH_REASON_SAVING` below, deliberately: it
 *  is the same save, seen from a different row. */
export const AGENT_SWITCH_REASON_SAVING = 'Saving…';

export interface RowSwitchGateInput {
  isForeignPrivate: boolean;
  workspaceDisabled: boolean;
  /** FRONT-602: `AgentRowModel.unavailable`. */
  unavailable: boolean;
  isAdmin: boolean;
  /** True while the bulk master switch's own request is in flight. */
  bulkSaving: boolean;
}

export interface RowSwitchGateResult {
  disabled: boolean;
  reason: string | null;
  /** Only true for the workspace-disabled reason, and only for an admin viewer. */
  showOrgSettingsLink: boolean;
}

/**
 * `disabled` and `reason` are derived from the SAME branch, so a state can
 * never be disabled with nothing said about why, or explained while still
 * live. Foreign-private takes precedence over workspace-disabled when a row
 * somehow satisfies both (an admin viewing another admin's disabled private
 * agent): the ownership reason is the more specific, more complete one.
 * `unavailable` (FRONT-602) is a third permanent lock, after those two.
 * `bulkSaving` is checked LAST, after every permanent-lock reason — a row
 * that was never switchable to begin with keeps its own, more informative
 * reason instead of switching to "Saving…" for a save that could never have
 * touched it (`masterSwitchTargetIds` only ever targets already-switchable
 * rows).
 */
export function rowSwitchGate(input: RowSwitchGateInput): RowSwitchGateResult {
  if (input.isForeignPrivate) {
    return { disabled: true, reason: AGENT_SWITCH_REASON_FOREIGN_PRIVATE, showOrgSettingsLink: false };
  }
  if (input.workspaceDisabled) {
    return {
      disabled: true,
      reason: AGENT_SWITCH_REASON_WORKSPACE_DISABLED,
      showOrgSettingsLink: input.isAdmin,
    };
  }
  if (input.unavailable) {
    return { disabled: true, reason: AGENT_SWITCH_REASON_UNAVAILABLE, showOrgSettingsLink: false };
  }
  if (input.bulkSaving) {
    return { disabled: true, reason: AGENT_SWITCH_REASON_SAVING, showOrgSettingsLink: false };
  }
  return { disabled: false, reason: null, showOrgSettingsLink: false };
}

// ---------------------------------------------------------------------------
// Master "All on for me" switch (FRONT-2406 round 2, section D) — acts on
// exactly the rows currently SHOWN (current tab AND current search). Same
// CLAUDE.md FRONT-1670 contract as `rowSwitchGate`: disabled and its reason
// are ONE decision, never two independently-drifting expressions.
// ---------------------------------------------------------------------------

export const MASTER_SWITCH_REASON_NONE_SWITCHABLE =
  'None of the agents shown can be switched on for you.';
/**
 * Shown instead of the "none switchable" reason while the bulk request is
 * in flight. A pending mutation is disabled-with-a-reason too — never a
 * second, independent `pending &&` check elsewhere — so the gate stays the
 * one place that decides whether the control is live (CLAUDE.md FRONT-1670:
 * "Optimistic update is NOT required; ... add reason 'Saving…' so the gate
 * stays the single source").
 */
export const MASTER_SWITCH_REASON_SAVING = 'Saving…';

export interface MasterSwitchRowInput {
  /** True iff this row's OWN switch is not locked (`!rowSwitchGate(...).disabled`). */
  switchable: boolean;
  /** This row's current "On for me" state. */
  personalOn: boolean;
}

export interface MasterSwitchGateResult {
  disabled: boolean;
  reason: string | null;
}

/**
 * `disabled` and `reason` are derived from the SAME branches, so — same
 * contract as `rowSwitchGate` — a state can never be disabled with nothing
 * said about why. `pending` takes precedence: mid-request, the switch is
 * disabled with "Saving…" regardless of what the (about-to-be-stale) row set
 * says.
 */
export function masterSwitchGate(rows: MasterSwitchRowInput[], pending: boolean): MasterSwitchGateResult {
  if (pending) return { disabled: true, reason: MASTER_SWITCH_REASON_SAVING };
  const anySwitchable = rows.some((r) => r.switchable);
  if (!anySwitchable) return { disabled: true, reason: MASTER_SWITCH_REASON_NONE_SWITCHABLE };
  return { disabled: false, reason: null };
}

/**
 * Checked iff every SWITCHABLE shown row is on. A locked row (e.g. someone
 * else's private agent) never counts either way — it cannot be turned on by
 * this control, so its own state shouldn't decide whether the control reads
 * as "all on". Zero switchable rows reads as unchecked (there is nothing
 * to claim is "all on"), which agrees with `masterSwitchGate` disabling the
 * control in that same case.
 */
export function isMasterSwitchChecked(rows: MasterSwitchRowInput[]): boolean {
  const switchable = rows.filter((r) => r.switchable);
  return switchable.length > 0 && switchable.every((r) => r.personalOn);
}

/**
 * FRONT-2406 round 2 review (item 5): the exact set of ids a click on the
 * master switch sends — pulled out as its own pure function, tested on its
 * own, so a change that widens it (e.g. targeting every SHOWN row instead of
 * only the switchable ones) is a one-line diff a unit test catches directly,
 * rather than something only a browser spec's PUT-body assertion would
 * eventually notice. Mirrors `isMasterSwitchChecked`'s own "locked rows
 * never count" rule: a row this control could not have turned on is not a
 * row it may send in the request either.
 */
export function masterSwitchTargetIds(shownRows: Array<{ id: string; switchable: boolean }>): string[] {
  return shownRows.filter((r) => r.switchable).map((r) => r.id);
}
